import { useEffect } from "react";
import { productCategories, CATEGORY_IMG, type Product } from "@/mocks/products";
import { siteData } from "@/store/siteData";

const CATEGORY_DESC: Record<string, string> = {
  laptop: "Kurumsal kullanıma uygun, taşınabilir performans sunan HP dizüstü bilgisayar. Ofis uygulamaları, çoklu görev ve mobil çalışma için idealdir.",
  desktop: "İş istasyonu ve ofis kullanımına yönelik güçlü HP masaüstü bilgisayar. Yüksek performans ve genişletilebilir donanım yapısı sunar.",
  aio: "Ekran ve bilgisayarı tek gövdede birleştiren şık HP tümleşik (all-in-one) bilgisayar. Yer kaplamayan, temiz bir çalışma alanı sağlar.",
  monitor: "Net görüntü ve geniş çalışma alanı sunan HP profesyonel monitör. Uzun süreli kullanımda göz konforu ve verimlilik için tasarlanmıştır.",
  other: "Kurumsal kullanıma uygun HP teknoloji ürünü.",
};

export default function ProductModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const email = siteData.getGeneralSettings().email || "info@c2tek.com.tr";
  const cat = productCategories.find((c) => c.slug === product.category);
  const title = product.desc || product.code;
  const subject = `Teklif Talebi: ${product.code}`;
  const body =
    `Merhaba,\n\nAşağıdaki ürün için teklif almak istiyorum:\n\n` +
    `Ürün: ${title}\n` +
    `Ürün Kodu: ${product.code}\n` +
    (product.dmo ? `DMO Ürün Kodu: ${product.dmo}\n` : "") +
    `Tip: ${product.type}\n` +
    `Marka: ${product.brand}\n\n` +
    `Ad Soyad:\nFirma:\nTelefon:\nAdet:\n\nTeşekkürler.`;
  const mailto = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const description =
    (CATEGORY_DESC[product.category] || CATEGORY_DESC.other) +
    ` ${product.brand} markalı bu ürün DMO kamu kataloğunda` +
    (product.dmo ? ` ${product.dmo} koduyla` : "") +
    ` yer alır; garanti ve HP yetkili servis kapsamındadır. Detaylı bilgi ve fiyat için "Teklif Al" ile bize ulaşabilirsiniz.`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div className="relative z-10 w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label="Kapat"
          className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-[#0F172A] shadow hover:bg-white cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>
        <div className="relative aspect-[16/10] bg-[#F1F5F9]">
          <img src={product.image || CATEGORY_IMG[product.category] || CATEGORY_IMG.other} alt={title} className="h-full w-full object-cover" />
          <span className="absolute left-3 top-3 rounded bg-[#0F172A] px-2.5 py-1 text-xs font-bold text-white">{product.brand}</span>
        </div>
        <div className="p-6">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest" style={{ color: cat?.color || "#3B82F6" }}>{product.type}</p>
          <h3 className="mb-4 text-lg font-bold leading-snug text-[#0F172A]">{title}</h3>
          <div className="mb-4">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-[#94A3B8]">Ürün Açıklaması</p>
            <p className="text-sm leading-relaxed text-[#475569]">{description}</p>
          </div>
          <div className="space-y-2 border-t border-[#E2E8F0] pt-4 text-sm">
            <div className="flex justify-between gap-4"><span className="text-[#64748B]">Ürün Kodu</span><span className="text-right font-medium text-[#0F172A]">{product.code}</span></div>
            {product.dmo && <div className="flex justify-between gap-4"><span className="text-[#64748B]">DMO Ürün Kodu</span><span className="font-medium text-[#0F172A]">{product.dmo}</span></div>}
            <div className="flex justify-between gap-4"><span className="text-[#64748B]">Marka</span><span className="font-medium text-[#0F172A]">{product.brand}</span></div>
            <div className="flex justify-between gap-4"><span className="text-[#64748B]">Kategori</span><span className="font-medium text-[#0F172A]">{cat?.label || product.type}</span></div>
          </div>
          <a
            href={mailto}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#3B82F6] py-3.5 font-semibold text-white transition-colors hover:bg-[#2563EB]"
          >
            <i className="ri-mail-send-line text-lg"></i> Teklif Al
          </a>
          <p className="mt-2 text-center text-xs text-[#94A3B8]">Ürün kodu ile e-posta otomatik hazırlanır</p>
        </div>
      </div>
    </div>
  );
}
